import { useState } from 'react'
import { MessageSquare, Mail, MessageCircle, Smartphone, BookOpen, Settings2, Pencil, Check, X } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Switch } from '@/components/ui/Switch'
import DeleteButton from '@/components/ui/DeleteButton'
import type ChannelSettings from '@/types/serializers/ChannelSettings'

const ICONS: Record<string, typeof MessageSquare> = {
  web_widget: MessageSquare,
  email: Mail,
  whatsapp: MessageCircle,
  messenger: MessageCircle,
  sms: Smartphone,
  help_center: BookOpen,
}

interface ChannelRowProps {
  channel: ChannelSettings
  onSave: (name: string) => void
  onToggleActive: (active: boolean) => void
  onConfigure: () => void
  onDelete: () => void
}

export function ChannelRow({ channel, onSave, onToggleActive, onConfigure, onDelete }: ChannelRowProps) {
  const Icon = ICONS[channel.channel_type] ?? MessageSquare
  const [editing, setEditing] = useState(false)
  const [name, setName] = useState(channel.name)

  const save = () => {
    setEditing(false)
    onSave(name.trim())
  }

  return (
    <div className="flex items-center justify-between rounded-lg border border-border p-3">
      <div className="flex min-w-0 items-center gap-3">
        <span className="flex size-9 shrink-0 items-center justify-center rounded-md bg-muted">
          <Icon className="size-4" />
        </span>
        {editing ? (
          <div className="flex items-center gap-1">
            <Input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') save()
                if (e.key === 'Escape') setEditing(false)
              }}
              className="h-8 w-56"
            />
            <Button variant="ghost" size="icon" onClick={save} className="size-8 cursor-pointer">
              <Check className="size-4" />
            </Button>
            <Button variant="ghost" size="icon" onClick={() => setEditing(false)} className="size-8 cursor-pointer">
              <X className="size-4" />
            </Button>
          </div>
        ) : (
          <div className="min-w-0">
            <p className="truncate font-medium">{channel.name}</p>
            <p className="text-xs text-muted-foreground">{channel.type_label}</p>
          </div>
        )}
      </div>
      {!editing && (
        <div className="flex items-center gap-2">
          <label className="flex cursor-pointer items-center gap-1.5 text-xs text-muted-foreground">
            <Switch checked={channel.active} onCheckedChange={onToggleActive} />
            {channel.active ? 'Active' : 'Off'}
          </label>
          <Button variant="outline" size="sm" onClick={() => { setName(channel.name); setEditing(true) }} className="cursor-pointer">
            <Pencil className="size-4 mr-1" />
            Rename
          </Button>
          <Button variant="outline" size="sm" onClick={onConfigure} className="cursor-pointer">
            <Settings2 className="size-4 mr-1" />
            Configure
          </Button>
          <DeleteButton onDelete={onDelete} />
        </div>
      )}
    </div>
  )
}
